// Your cookbook: title, about, sections (title, note, recipes). Edit mode adds/removes
// sections and reorders sections and recipes through menus, so the page keeps its layout:
// texts become fields in place, the section menus and "add section" fade in on kept spots.
import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useNav, type DetailParams } from '../navigation';
import { useDiet, useHeader } from '../hooks';
import { newId, useLibrary, useRecipeLookup } from '../store/library';
import { Carousel } from '../components/Cards';
import Photo from '../components/Photo';
import { RecipePickerSheet } from '../components/Pickers';
import { showMenu } from '../components/Dialogs';
import { EditableText, Fade } from '../components/Motion';
import { applyPickedMedia } from '../logic/mediaDraft';
import { pickPhoto, savePhoto } from '../platform/media';
import { Button, Caption, EmptyState, IconButton, Screen, Section } from '../components/ui';
import { font, spacing, usePalette } from '../theme';
import type { Cookbook, Recipe } from '../types';

function move<T>(list: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export default function CookbookScreen({ route, navigation }: NativeStackScreenProps<DetailParams, 'Cookbook'>) {
  const { t } = useTranslation();
  const nav = useNav();
  const p = usePalette();
  const book = useLibrary(s => s.cookbooks[route.params.id]);
  const put = useLibrary(s => s.put);
  const lookup = useRecipeLookup();
  const diet = useDiet();
  const [editing, setEditing] = useState(!!route.params.edit);
  const [draft, setDraft] = useState<Cookbook | undefined>(book);
  const latest = useRef(draft);
  latest.current = draft;
  const [pickFor, setPickFor] = useState<string | null>(null);
  // Sections whose note line is shown while editing (notes that exist always show).
  const [noteLines, setNoteLines] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!editing) setNoteLines(new Set());
  }, [editing]);

  useEffect(() => {
    if (!editing) setDraft(book);
  }, [book, editing]);
  useEffect(() => navigation.addListener('beforeRemove', () => editing && draft && put('cookbooks', draft)), [navigation, editing, draft, put]);

  const c = editing ? draft : book;
  useHeader(
    {
      title: book?.title ?? '',
      headerRight: () =>
        editing ? (
          <IconButton icon="check" label={t('common.done')} onPress={() => { if (draft) put('cookbooks', draft); setEditing(false); }} />
        ) : (
          <IconButton icon="edit" label={t('common.edit')} onPress={() => { setDraft(book); setEditing(true); }} />
        ),
    },
    [editing, draft, book, t],
  );
  if (!c) return <EmptyState icon="cookbook" title={t('common.empty')} />;

  const update = (patch: Partial<Cookbook>) => {
    const current = latest.current;
    if (!current) return;
    latest.current = { ...current, ...patch };
    setDraft(latest.current);
  };
  const updateSection = (id: string, patch: Partial<Cookbook['sections'][number]>) => update({ sections: c.sections.map(s => (s.id === id ? { ...s, ...patch } : s)) });

  const sectionMenu = (si: number) => {
    const section = c.sections[si];
    showMenu(
      [
        { label: t('common.add'), icon: 'plus', onPress: () => setPickFor(section.id) },
        ...(!section.note && !noteLines.has(section.id) ? [{ label: t('recipe.addNote'), icon: 'edit' as const, onPress: () => setNoteLines(n => new Set(n).add(section.id)) }] : []),
        ...(si > 0 ? [{ label: t('recipe.moveUp'), icon: 'up' as const, onPress: () => update({ sections: move(c.sections, si, -1) }) }] : []),
        ...(si < c.sections.length - 1 ? [{ label: t('recipe.moveDown'), icon: 'down' as const, onPress: () => update({ sections: move(c.sections, si, 1) }) }] : []),
        { label: t('common.delete'), icon: 'trash', onPress: () => update({ sections: c.sections.filter(s => s.id !== section.id) }) },
      ],
      section.title || undefined,
    );
  };
  const recipeMenu = (sectionId: string, rid: string) => {
    const section = c.sections.find(s => s.id === sectionId);
    if (!section) return;
    const ri = section.recipeIds.indexOf(rid);
    showMenu(
      [
        ...(ri > 0 ? [{ label: t('recipe.moveLeft'), icon: 'back' as const, onPress: () => updateSection(sectionId, { recipeIds: move(section.recipeIds, ri, -1) }) }] : []),
        ...(ri < section.recipeIds.length - 1 ? [{ label: t('recipe.moveRight'), icon: 'forward' as const, onPress: () => updateSection(sectionId, { recipeIds: move(section.recipeIds, ri, 1) }) }] : []),
        { label: t('common.delete'), icon: 'trash', onPress: () => updateSection(sectionId, { recipeIds: section.recipeIds.filter(x => x !== rid) }) },
      ],
      lookup(rid)?.title,
    );
  };

  return (
    <View style={{ flex: 1 }}>
      <Screen>
        <View>
          <Photo media={c.image} seed={c.id} icon="cookbook" style={{ width: '100%', aspectRatio: 2.5 }} />
          <Fade visible={editing} style={{ position: 'absolute', right: spacing.s, bottom: spacing.s }}>
            <Button label={t('recipe.heroMedia')} icon="camera" variant="secondary" style={{ minHeight: 40 }} onPress={() => applyPickedMedia(pickPhoto, savePhoto, () => useLibrary.getState().cookbooks[c.id] ? latest.current : undefined, (current, image) => { update({ image }); put('cookbooks', { ...current, image }); })} iconOnly />
          </Fade>
        </View>
        <EditableText editing={editing} value={c.title} onChangeText={v => update({ title: v.replace(/\n/g, ' ') })} accessibilityLabel={t('editors.title')}
          style={{ fontSize: font.title, fontWeight: '700', color: p.text, lineHeight: 34 }} />
        <Section title={t('recipe.aboutCookbook')}>
          <EditableText editing={editing} value={c.explainer} onChangeText={v => update({ explainer: v })} placeholder={t('common.notes')} style={{ fontSize: font.body, color: p.text, lineHeight: 22 }} />
        </Section>
        {c.sections.map((section, si) => {
          const recipes = section.recipeIds.map(id => lookup(id)).filter((r): r is Recipe => !!r && !diet.hidden(r));
          return (
            <View key={section.id} style={{ gap: spacing.s }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.s }}>
                <View style={{ flex: 1 }}>
                  <EditableText editing={editing} value={section.title} onChangeText={v => updateSection(section.id, { title: v.replace(/\n/g, ' ') })} placeholder={t('library.defaultSection')}
                    style={{ fontSize: font.title3, fontWeight: '700', color: p.text, lineHeight: 26 }} />
                </View>
                <Fade visible={editing}>
                  <IconButton icon="more" label={t('common.more')} onPress={() => sectionMenu(si)} />
                </Fade>
              </View>
              {section.note || (editing && noteLines.has(section.id)) ? (
                <EditableText editing={editing} value={section.note} onChangeText={v => updateSection(section.id, { note: v })} placeholder={t('common.notes')}
                  style={{ fontSize: font.footnote, color: p.secondaryText, lineHeight: 18 }} />
              ) : null}
              {recipes.length ? (
                <Carousel
                  items={recipes.map(r => ({ id: r.id, title: r.title, subtitle: t('common.minutes', { count: r.durationMin }), media: r.image, icon: 'recipe', diets: diet.issues(r) }))}
                  onPress={id => (editing ? recipeMenu(section.id, id) : nav.navigate('Recipe', { id }))}
                />
              ) : (
                <Caption>{t('common.empty')}</Caption>
              )}
            </View>
          );
        })}
        <Fade visible={editing}>
          <Button label={`${t('common.add')}: ${t('library.defaultSection')}`} icon="plus" variant="plain" onPress={() => update({ sections: [...c.sections, { id: newId(), title: '', note: '', recipeIds: [] }] })} iconOnly />
        </Fade>
      </Screen>
      <RecipePickerSheet
        visible={pickFor !== null}
        onClose={() => setPickFor(null)}
        onPick={r => {
          const section = c.sections.find(s => s.id === pickFor);
          if (section && !c.sections.some(s => s.recipeIds.includes(r.id))) updateSection(section.id, { recipeIds: [...section.recipeIds, r.id] });
          setPickFor(null);
        }}
      />
    </View>
  );
}
